import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/ui/page-header';
import { HelpButton } from '../components/ui/help-button';
import { FeedbackButton } from '../components/ui/feedback-button';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { KpiStrip } from '../components/ui/kpi-strip';
import { RightPanel } from '../components/ui/right-panel';
import { ChainVisual } from '../components/ui/chain-visual';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  CheckSquare,
  Search,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Clock,
  CheckCircle,
  PlayCircle,
  Shield,
  Users,
  Cpu,
  ClipboardList,
  Truck,
  Link2,
  HandHelping,
  ArrowRight,
  ArrowLeft,
  Building2,
  Globe,
  Star,
  Mail,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  Check,
  Lock,
  Layers,
  ExternalLink,
  Zap,
  ArrowUpDown
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import { ContextualTip } from '../components/ContextualTip';
import DecisionRow from '../components/DecisionRow';
import { Collapsible } from '../components/Collapsible';
import { PageError } from '../components/PageError';
import { Coachmark } from '../components/Coachmark';
import { MeasureFormDialog, HelpWizardDialog, FrameworkWizardDialog } from '../components/measures/MeasureDialogs';

import { PageShell, PageToolbar, SectionTabs, SECTIONS } from '../components/enterprise';

const MeasuresPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [measures, setMeasures] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [marketplaceSuppliers, setMarketplaceSuppliers] = useState([]);
  const [risks, setRisks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [sortField, setSortField] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  const toggleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMeasure, setEditingMeasure] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    goal: '',
    domain: 'technology',
    complexity: 'medium',
    impact: 3,
    cost_indication: '',
    linked_risk_ids: [],
    supplier_id: ''
  });

  // Hulp inschakelen wizard state
  const [helpWizardOpen, setHelpWizardOpen] = useState(false);
  const [helpWizardStep, setHelpWizardStep] = useState(1);
  const [selectedMeasureForHelp, setSelectedMeasureForHelp] = useState(null);
  const [helpRequestData, setHelpRequestData] = useState({
    help_type: 'implementatiehulp',
    summary: '',
    desired_outcome: '',
    urgency: 'normaal',
    share_company_size: false,
    share_sector: false,
    share_region: false,
    share_action_context: true,
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const [matchedSuppliers, setMatchedSuppliers] = useState([]);
  const [selectedSupplierForHelp, setSelectedSupplierForHelp] = useState(null);
  const [detailMeasure, setDetailMeasure] = useState(null);
  const [createdRequestId, setCreatedRequestId] = useState(null);

  // Framework wizard state
  const [fwWizardOpen, setFwWizardOpen] = useState(false);
  const [fwStep, setFwStep] = useState(0);
  const [frameworks, setFrameworks] = useState([]);
  const [activeModules, setActiveModules] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [frameworkControls, setFrameworkControls] = useState([]);
  const [selectedControls, setSelectedControls] = useState([]);
  const [fwCreating, setFwCreating] = useState(false);
  const [controlSearch, setControlSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setFetchError(false);
    try {
      const [measuresRes, suppliersRes, risksRes, marketplaceRes, fwRes, modRes] = await Promise.all([
        api.get('/ggrc/measures'),
        api.get('/partners'),
        api.get('/ggrc/risks'),
        api.get('/marketplace/suppliers').catch(() => ({ data: { suppliers: [] } })),
        api.get('/frameworks').catch(() => ({ data: [] })),
        api.get('/modules').catch(() => ({ data: [] })),
      ]);
      setMeasures(measuresRes.data.measures || []);
      setSuppliers(suppliersRes.data || []);
      setRisks(risksRes.data.risks || []);
      setMarketplaceSuppliers(marketplaceRes.data.suppliers || []);
      setFrameworks(fwRes.data || []);
      setActiveModules((modRes.data || []).filter(m => m.is_subscribed));
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingMeasure(null);
    setWizardStep(1);

    // Smart defaults: vul velden in op basis van het eerste ongedekte risico
    const uncoveredRisk = risks.find(r => {
      const hasMeasure = measures.some(m => (m.linked_risk_ids || []).includes(r.id));
      return !hasMeasure && (r.risk_level === 'Hoog' || r.risk_level === 'Kritiek' || r.risk_level === 'Midden');
    });

    if (uncoveredRisk) {
      const domain = uncoveredRisk.domain === 'people' ? 'people' : uncoveredRisk.domain === 'process' ? 'process' : 'technology';
      const score = (uncoveredRisk.probability || 3) * (uncoveredRisk.impact || 3);
      const suggestedName = `Maatregel voor: ${uncoveredRisk.title}`;
      const suggestedGoal = score >= 20 
        ? 'Directe risicovermindering door implementatie van beveiligingscontroles'
        : score >= 10 
        ? 'Vermindering van risico door aanpassing van processen of systemen'
        : 'Structurele verbetering van beveiligingsniveau';

      setFormData({
        name: suggestedName,
        description: '',
        goal: suggestedGoal,
        domain,
        complexity: score >= 20 ? 'high' : score >= 10 ? 'medium' : 'low',
        impact: Math.min(5, Math.ceil(score / 5)),
        cost_indication: '',
        linked_risk_ids: [uncoveredRisk.id],
        linked_asset_ids: [],
        linked_vuln_ids: [],
        linked_threat_ids: [],
        supplier_id: '',
        status: 'planned',
        framework: '',
        control_ref: '',
        _smartDefault: true,
        _linkedRiskTitle: uncoveredRisk.title,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        goal: '',
        domain: 'technology',
        complexity: 'medium',
        impact: 3,
        cost_indication: '',
        linked_risk_ids: [],
        linked_asset_ids: [],
        linked_vuln_ids: [],
        linked_threat_ids: [],
        supplier_id: '',
        status: 'planned',
        framework: '',
        control_ref: '',
      });
    }
    setModalOpen(true);
  };

  const openEditModal = (measure) => {
    setEditingMeasure(measure);
    setWizardStep(1);
    setFormData({
      name: measure.name,
      description: measure.description || '',
      goal: measure.goal || '',
      domain: measure.domain || 'technology',
      complexity: measure.complexity || 'medium',
      impact: measure.impact || 3,
      cost_indication: measure.cost_indication || '',
      linked_risk_ids: measure.linked_risk_ids || [],
      linked_asset_ids: measure.linked_asset_ids || [],
      linked_vuln_ids: measure.linked_vuln_ids || [],
      linked_threat_ids: measure.linked_threat_ids || [],
      supplier_id: measure.supplier_id || '',
      status: measure.status || 'planned',
      framework: measure.framework || '',
      control_ref: measure.control_ref || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Naam is verplicht');
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        ...formData,
        cost_indication: formData.cost_indication ? parseFloat(formData.cost_indication) : null,
        supplier_id: formData.supplier_id || null,
        framework: formData.framework || null,
        control_ref: formData.control_ref || null,
      };

      if (editingMeasure) {
        await api.patch(`/ggrc/measures/${editingMeasure.id}`, payload);
        toast.success('Maatregel bijgewerkt');
      } else {
        await api.post('/ggrc/measures', payload);
        toast.success('Maatregel aangemaakt');
      }
      
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De actie kon niet worden uitgevoerd. Probeer het later opnieuw.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (measureId) => {
    if (!window.confirm('Weet je zeker dat je deze oplossing wilt verwijderen?')) return;
    
    try {
      await api.delete(`/ggrc/measures/${measureId}`);
      toast.success('Maatregel verwijderd');
      fetchData();
    } catch (error) {
      toast.error('De oplossing kon niet worden verwijderd. Probeer het later opnieuw.');
    }
  };

  const handleStatusChange = async (measureId, newStatus) => {
    try {
      await api.patch(`/ggrc/measures/${measureId}`, { status: newStatus });
      toast.success('Status bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('De status kon niet worden bijgewerkt. Probeer het later opnieuw.');
    }
  };

  // Hulp inschakelen wizard functions
  const openHelpWizard = (measure) => {
    setSelectedMeasureForHelp(measure);
    setHelpWizardStep(1);
    setHelpRequestData({
      help_type: 'implementatiehulp',
      summary: `Hulp nodig bij: ${measure.name}`,
      desired_outcome: measure.goal || '',
      urgency: 'normaal',
      share_company_size: false,
      share_sector: false,
      share_region: false,
      share_action_context: true,
      contact_name: user?.full_name || '',
      contact_email: user?.email || '',
      contact_phone: '',
      selected_measure_ids: [measure.id]
    });
    setMatchedSuppliers([]);
    setSelectedSupplierForHelp(null);
    setCreatedRequestId(null);
    setHelpWizardOpen(true);
  };

  const toggleMeasureSelection = (measureId) => {
    setHelpRequestData(prev => {
      const ids = prev.selected_measure_ids || [];
      if (ids.includes(measureId)) {
        return { ...prev, selected_measure_ids: ids.filter(id => id !== measureId) };
      }
      return { ...prev, selected_measure_ids: [...ids, measureId] };
    });
  };

  const handleHelpWizardNext = async () => {
    if (helpWizardStep === 1) {
      // Validate step 1
      if (!helpRequestData.summary.trim()) {
        toast.error('Beschrijf uw hulpvraag');
        return;
      }
      if (!(helpRequestData.selected_measure_ids || []).length) {
        toast.error('Selecteer minimaal een oplossing');
        return;
      }
      setHelpWizardStep(2);
    } else if (helpWizardStep === 2) {
      // Validate step 2 and create request
      if (!helpRequestData.contact_name || !helpRequestData.contact_email) {
        toast.error('Vul uw contactgegevens in');
        return;
      }
      
      setActionLoading(true);
      try {
        // Create the marketplace request
        const response = await api.post('/marketplace/requests', {
          ...helpRequestData,
          action_id: selectedMeasureForHelp?.id
        });
        
        setCreatedRequestId(response.data.request_id);
        
        // Get matched suppliers
        const matchResponse = await api.get(`/marketplace/requests/${response.data.request_id}/match`);
        setMatchedSuppliers(matchResponse.data.suppliers || []);
        
        setHelpWizardStep(3);
      } catch (error) {
        toast.error(getUserErrorMessage(error, 'Uw hulpverzoek kon niet worden aangemaakt. Probeer het later opnieuw.'));
      } finally {
        setActionLoading(false);
      }
    } else if (helpWizardStep === 3) {
      // Select supplier and submit
      if (!selectedSupplierForHelp) {
        toast.error('Selecteer een leverancier');
        return;
      }
      
      setActionLoading(true);
      try {
        await api.post(`/marketplace/requests/${createdRequestId}/select`, {
          supplier_org_id: selectedSupplierForHelp.id
        });
        
        toast.success('Aanvraag verstuurd naar leverancier!');
        setHelpWizardOpen(false);
        
        // Reset state
        setSelectedMeasureForHelp(null);
        setHelpWizardStep(1);
        setMatchedSuppliers([]);
        setSelectedSupplierForHelp(null);
        setCreatedRequestId(null);
      } catch (error) {
        toast.error(getUserErrorMessage(error, 'De leverancier kon niet worden gekoppeld. Probeer het later opnieuw.'));
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleHelpWizardBack = () => {
    if (helpWizardStep > 1) {
      setHelpWizardStep(helpWizardStep - 1);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      planned: { label: 'Gepland', class: 'bg-neutral-100 text-neutral-700', icon: Clock },
      in_progress: { label: 'In uitvoering', class: 'bg-blue-50 text-blue-700', icon: PlayCircle },
      implemented: { label: 'Geïmplementeerd', class: 'bg-green-50 text-green-700', icon: CheckCircle },
      verified: { label: 'Geverifieerd', class: 'bg-emerald-50 text-emerald-700', icon: Shield }
    };
    const { label, class: className, icon: Icon } = config[status] || config.planned;
    return (
      <Badge className={`${className} border-transparent`}>
        <Icon className="h-3 w-3 mr-1" strokeWidth={1.5} />
        {label}
      </Badge>
    );
  };

  const getDomainIcon = (domain) => {
    const icons = {
      people: Users,
      process: ClipboardList,
      technology: Cpu
    };
    return icons[domain] || Cpu;
  };

  const getComplexityBadge = (complexity) => {
    const config = {
      low: { label: 'Eenvoudig', class: 'bg-green-50 text-green-700' },
      medium: { label: 'Gemiddeld', class: 'bg-yellow-50 text-yellow-700' },
      high: { label: 'Complex', class: 'bg-red-50 text-red-700' }
    };
    const { label, class: className } = config[complexity] || config.medium;
    return <Badge className={`${className} border-transparent text-xs`}>{label}</Badge>;
  };

  // Framework helpers
  const MODULE_FRAMEWORK_MAP = {
    'iso27001': 'iso27001_2022',
    'nis2_compliance': 'nis2',
  };

  const getAvailableFrameworks = () => {
    // Frameworks linked to active modules
    return frameworks.filter(fw => {
      return activeModules.some(mod => MODULE_FRAMEWORK_MAP[mod.id] === fw.id);
    });
  };

  const getAllFrameworks = () => frameworks;

  const getFrameworkLabel = (fwId) => {
    const fw = frameworks.find(f => f.id === fwId);
    return fw?.name || fwId;
  };

  const openFrameworkWizard = async () => {
    setFwStep(0);
    setSelectedFramework(null);
    setFrameworkControls([]);
    setSelectedControls([]);
    setControlSearch('');
    setFwWizardOpen(true);
  };

  const handleSelectFramework = async (fw) => {
    setSelectedFramework(fw);
    try {
      const res = await api.get(`/frameworks/${fw.id}`);
      setFrameworkControls(res.data?.controls || []);
    } catch {
      setFrameworkControls([]);
    }
    setFwStep(1);
  };

  const toggleControl = (controlId) => {
    setSelectedControls(prev =>
      prev.includes(controlId) ? prev.filter(c => c !== controlId) : [...prev, controlId]
    );
  };

  const handleCreateFromControls = async () => {
    if (selectedControls.length === 0) {
      toast.error('Selecteer minimaal één control');
      return;
    }
    setFwCreating(true);
    try {
      const controlsToCreate = frameworkControls.filter(c => selectedControls.includes(c.id));
      for (const ctrl of controlsToCreate) {
        const domainMap = { 'Organisatorisch': 'process', 'Technologisch': 'technology', 'Menselijk': 'people' };
        await api.post('/ggrc/measures', {
          name: `${ctrl.code}: ${ctrl.name}`,
          description: ctrl.description,
          goal: `Voldoen aan ${selectedFramework.name} control ${ctrl.code}`,
          domain: domainMap[ctrl.category] || 'process',
          complexity: 'medium',
          impact: 3,
          framework_id: selectedFramework.id,
          control_id: ctrl.id,
          linked_risk_ids: [],
        });
      }
      toast.success(`${controlsToCreate.length} maatregel(en) aangemaakt vanuit ${selectedFramework.name}`);
      setFwWizardOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De maatregel kon niet worden aangemaakt. Controleer uw invoer en probeer het opnieuw.'));
    } finally {
      setFwCreating(false);
    }
  };

  // Filter measures
  const filteredMeasures = measures.filter(measure => {
    const matchesSearch = 
      measure.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (measure.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || measure.status === statusFilter;
    const matchesDomain = domainFilter === 'all' || measure.domain === domainFilter;
    return matchesSearch && matchesStatus && matchesDomain;
  }).sort((a, b) => {
    if (sortField) {
      let aVal, bVal;
      switch (sortField) {
        case 'id': aVal = a.measure_id || ''; bVal = b.measure_id || ''; break;
        case 'name': aVal = a.name || ''; bVal = b.name || ''; break;
        case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
        case 'complexity': aVal = a.complexity || ''; bVal = b.complexity || ''; break;
        case 'impact': aVal = a.effectiveness || 0; bVal = b.effectiveness || 0; break;
        default: return 0;
      }
      if (typeof aVal === 'string') return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortAsc ? aVal - bVal : bVal - aVal;
    }
    // Default: planned first, then in_progress, then rest
    const statusOrder = { planned: 0, in_progress: 1, implemented: 2, verified: 3 };
    return (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4);
  });

  // Stats
  const stats = {
    total: measures.length,
    planned: measures.filter(m => m.status === 'planned').length,
    in_progress: measures.filter(m => m.status === 'in_progress').length,
    implemented: measures.filter(m => m.status === 'implemented' || m.status === 'verified').length
  };

  if (loading) {
    return <PageShell section="verbeteren" testId="measures-page" loading />;
  }

  if (fetchError) {
    return (
      <PageShell section="verbeteren" testId="measures-page">
        <div className="px-6 py-10">
          <PageError onRetry={() => { setLoading(true); fetchData(); }} />
        </div>
      </PageShell>
    );
  }

  // Human-readable helpers
  const getMeasureExplanation = (m) => {
    const linkedCount = (m.linked_risk_ids || []).length;
    const linkedText = linkedCount > 0 ? `Lost ${linkedCount} risico${linkedCount !== 1 ? "'s" : ''} op` : '';
    if (m.status === 'implemented' || m.status === 'verified') return linkedText || 'Deze oplossing is actief';
    if (m.status === 'in_progress') return linkedText ? `${linkedText}, in uitvoering` : 'Hier wordt momenteel aan gewerkt';
    return linkedText ? `${linkedText}, nog niet gestart` : 'Deze oplossing is nog niet gestart';
  };

  const getMeasureCta = (m) => {
    if (m.status === 'planned') return 'Start uitvoering';
    if (m.status === 'in_progress') return 'Bekijk voortgang';
    return 'Bekijk details';
  };

  const getMeasureStatusLabel = (m) => {
    const labels = { planned: 'Nog niet gestart', in_progress: 'In uitvoering', implemented: 'Actief', verified: 'Geverifieerd' };
    return labels[m.status] || m.status;
  };

  return (
    <PageShell section="verbeteren" testId="measures-page">
      <PageToolbar
        title="Maatregelen"
        subtitle={stats.total === 0 ? 'Nog geen oplossingen gekozen'
          : stats.planned > 0 ? `${stats.planned} geplande ${stats.planned === 1 ? 'maatregel' : 'maatregelen'} wachten op uitvoering`
          : stats.in_progress > 0 ? 'Maatregelen zijn in uitvoering'
          : 'Alle maatregelen zijn uitgevoerd'}
        count={stats.total > 0 ? stats.total : undefined}
        onAdd={() => { openCreateModal(); }}
        addLabel="Maatregel toevoegen"
        testId="measures"
      />

      <div className="px-6 py-6 space-y-8">
          <ContextualTip pageKey="measures" tip="Verander de status van een oplossing naar 'Actief' zodra je ermee begint. Zo houd je je voortgang bij." />

          <div className="space-y-8">
            {/* STATUS */}
            <section className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-7" data-testid="measures-status">
              <p className="spk-section-label">Status</p>
              <Coachmark
                id="measures-intro"
                text="Maatregelen zijn de oplossingen voor je risico's. Begin met de maatregel met het label 'Begin hiermee': die heeft de meeste impact."
                className="mt-2 mb-2"
              />
              {stats.total === 0 ? (
                <>
                  <h2 className="text-2xl font-semibold mt-2">Er zijn nog geen oplossingen gekozen</h2>
                  <p className="mt-3 text-neutral-600">
                    Ga naar de risico's om te zien waar actie nodig is, of voeg handmatig een maatregel toe.
                  </p>
                </>
              ) : stats.planned > 0 ? (
                <>
                  <h2 className="text-2xl font-semibold mt-2">
                    {stats.planned} {stats.planned === 1 ? 'oplossing wacht' : 'oplossingen wachten'} nog op uitvoering
                  </h2>
                  <p className="mt-3 text-neutral-600">
                    Zonder uitvoering blijven de bijbehorende risico's bestaan.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold mt-2">Alle oplossingen zijn in uitvoering of afgerond</h2>
                  <p className="mt-3 text-neutral-600">Goed bezig! Controleer periodiek of alles nog actueel is.</p>
                </>
              )}

              {stats.planned > 0 && (
                <div className="mt-6 rounded-xl border border-neutral-200 p-4" data-testid="planned-block">
                  <p className="font-medium">{stats.planned} {stats.planned === 1 ? 'maatregel is' : 'maatregelen zijn'} gepland maar nog niet gestart</p>
                  <p className="text-sm text-neutral-600 mt-2">
                    Start de uitvoering om je beveiliging te verbeteren.
                  </p>
                  <Button
                    onClick={() => {
                      const firstPlanned = measures.find(m => m.status === 'planned');
                      if (firstPlanned) setDetailMeasure(firstPlanned);
                    }}
                    className="mt-4 bg-black text-white hover:bg-neutral-800"
                    data-testid="start-first-measure"
                  >
                    Start de eerste maatregel
                    <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </div>
              )}
            </section>

            {/* MEASURE LIST */}
            {measures.length > 0 ? (
              <section className="rounded-2xl border border-neutral-200 p-7" data-testid="measures-list">
                <div className="spk-filter-bar" data-testid="measure-filters">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                    <input
                      type="text"
                      placeholder="Zoek in oplossingen..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                      data-testid="measure-search-input"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40 border-neutral-200" data-testid="measure-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle statussen</SelectItem>
                      <SelectItem value="planned">Gepland</SelectItem>
                      <SelectItem value="in_progress">In uitvoering</SelectItem>
                      <SelectItem value="implemented">Uitgevoerd</SelectItem>
                      <SelectItem value="verified">Geverifieerd</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={domainFilter} onValueChange={setDomainFilter}>
                    <SelectTrigger className="w-36 border-neutral-200" data-testid="measure-domain-filter">
                      <SelectValue placeholder="Domein" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle domeinen</SelectItem>
                      <SelectItem value="technisch">Technisch</SelectItem>
                      <SelectItem value="organisatorisch">Organisatorisch</SelectItem>
                      <SelectItem value="fysiek">Fysiek</SelectItem>
                    </SelectContent>
                  </Select>
                  {(searchQuery || statusFilter !== 'all' || domainFilter !== 'all') && (
                    <button onClick={() => { setSearchQuery(''); setStatusFilter('all'); setDomainFilter('all'); }} className="text-xs text-neutral-500 hover:text-black" data-testid="measure-clear-filters">
                      Wis filters
                    </button>
                  )}
                  <div className="flex-1" />
                  <Button
                    onClick={() => openCreateModal()}
                    className="bg-black text-white hover:bg-neutral-800"
                    data-testid="add-measure-btn"
                  >
                    <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                    Oplossing toevoegen
                  </Button>
                </div>
                {filteredMeasures.length > 0 && (
                  <p className="text-sm text-neutral-500 mb-4">
                    {filteredMeasures.length === 1 ? '1 oplossing' : `${filteredMeasures.length} oplossingen`}
                  </p>
                )}

                {filteredMeasures.length > 0 ? (
                  <div className="space-y-3">
                    {filteredMeasures.map((measure, i) => {
                      const explanation = getMeasureExplanation(measure);
                      const cta = getMeasureCta(measure);
                      const statusLabel = getMeasureStatusLabel(measure);
                      const isPlanned = measure.status === 'planned';
                      const isFirstPlanned = isPlanned && filteredMeasures.slice(0, i).every(m => m.status !== 'planned');

                      return (
                        <div
                          key={measure.id}
                          className={`spk-list-item ${isPlanned ? 'border-amber-200 bg-amber-50/20' : 'border-neutral-200'}`}
                          onClick={() => setDetailMeasure(measure)}
                          data-testid={`measure-row-${measure.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-black truncate">{measure.name}</p>
                              {isFirstPlanned && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white shrink-0" data-testid="measure-begin-hiermee">Begin hiermee</span>
                              )}
                              {(measure.linked_risk_ids || []).length > 0 && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 shrink-0" data-testid={`measure-risk-count-${measure.id}`}>
                                  {(measure.linked_risk_ids || []).length} risico{(measure.linked_risk_ids || []).length !== 1 ? "'s" : ''}
                                </span>
                              )}
                            </div>
                            {measure.description && (
                              <p className="text-sm text-neutral-500 line-clamp-1">{measure.description}</p>
                            )}
                            <p className="text-sm text-neutral-600 mt-1">{explanation}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                                measure.status === 'implemented' || measure.status === 'verified' ? 'bg-green-100 text-green-700' :
                                measure.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-neutral-100 text-neutral-600'
                              }`}>
                                {statusLabel}
                              </span>
                              {measure.cost_indication && (
                                <span className="text-[11px] text-neutral-400">{'\u20AC'}{measure.cost_indication.toLocaleString()}</span>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-neutral-300 shrink-0" strokeWidth={1.5} />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-neutral-500 text-center py-4">Geen resultaten voor je zoekopdracht</p>
                )}
              </section>
            ) : (
              <section className="rounded-2xl border-2 border-black p-6 text-center" data-testid="empty-measures">
                <h2 className="text-2xl font-semibold mt-2">Begin met het kiezen van oplossingen</h2>
                <p className="mt-3 text-neutral-600 max-w-md mx-auto">
                  Ga naar de risico's en kies per risico een passende maatregel, of voeg er handmatig een toe.
                </p>
                <div className="flex justify-center gap-3 mt-6">
                  <Button onClick={() => navigate('/risks')} variant="outline" className="border-black text-black">
                    Bekijk risico's
                  </Button>
                  <Button onClick={() => openCreateModal()} className="bg-black text-white hover:bg-neutral-800">
                    <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                    Handmatig toevoegen
                  </Button>
                </div>
              </section>
            )}
            {/* NEXT STEP */}
            {measures.length > 0 && (
              <section className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-7" data-testid="measures-next-step">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="spk-section-label mb-0">Volgende stap</p>
                      {stats.implemented > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black text-white">Aanbevolen</span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold mt-1">Bouw je beveiligingsstrategie</h3>
                    <p className="text-sm text-neutral-600 mt-1">Bekijk je volwassenheidsniveau en plan structurele verbeteringen voor de lange termijn.</p>
                  </div>
                  <Button
                    onClick={() => navigate('/strategy')}
                    className="bg-black text-white hover:bg-neutral-800 shrink-0"
                    data-testid="measures-goto-strategy"
                  >
                    Bekijk strategie
                    <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
                  </Button>
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Measure Detail Right Panel */}
        <RightPanel
          open={!!detailMeasure}
          onClose={() => setDetailMeasure(null)}
          title={detailMeasure?.name || 'Maatregel'}
          subtitle={detailMeasure ? `${detailMeasure.measure_id} · ${detailMeasure.domain === 'people' ? 'Mens' : detailMeasure.domain === 'process' ? 'Proces' : 'Technologie'}` : ''}
          testId="measure-detail-panel"
          chain={
            <ChainVisual
              activeStep="measure"
              data={{
                asset: detailMeasure?.linked_asset_ids?.length ? `${detailMeasure.linked_asset_ids.length} assets` : '',
                vulnerability: detailMeasure?.linked_vuln_ids?.length ? `${detailMeasure.linked_vuln_ids.length} kwetsb.` : '',
                threat: detailMeasure?.linked_threat_ids?.length ? `${detailMeasure.linked_threat_ids.length} dreig.` : '',
                risk: detailMeasure?.linked_risk_ids?.length ? `${detailMeasure.linked_risk_ids.length} risico's` : '',
                measure: detailMeasure?.name || '',
              }}
              testId="measure-chain-visual"
            />
          }
          actions={
            <>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => { openEditModal(detailMeasure); setDetailMeasure(null); }} data-testid="detail-edit-btn">
                <Edit className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                Bewerken
              </Button>
              {detailMeasure?.status === 'planned' && (
                <Button variant="outline" size="sm" onClick={() => { handleStatusChange(detailMeasure.id, 'in_progress'); setDetailMeasure(null); }} className="border-green-200 text-green-700">
                  <CheckCircle className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                  Zelf uitvoeren
                </Button>
              )}
              {marketplaceSuppliers.length > 0 && detailMeasure?.status !== 'verified' && (
                <Button variant="outline" size="sm" onClick={() => { openHelpWizard(detailMeasure); setDetailMeasure(null); }} className="border-blue-200 text-blue-600">
                  <HandHelping className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                  Hulp vragen
                </Button>
              )}
            </>
          }
        >
          {detailMeasure && (
            <div className="space-y-6" data-testid="measure-detail-popup">
              <div className="flex items-center gap-3 flex-wrap">
                {getStatusBadge(detailMeasure.status)}
                {getComplexityBadge(detailMeasure.complexity)}
                {detailMeasure.framework_id && (
                  <Badge variant="outline" className="border-blue-200 text-blue-600 text-xs">
                    <Shield className="h-3 w-3 mr-1" strokeWidth={1.5} />
                    {getFrameworkLabel(detailMeasure.framework_id)}{detailMeasure.control_id && ` · ${detailMeasure.control_id}`}
                  </Badge>
                )}
              </div>

              {detailMeasure.description && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Beschrijving</p>
                  <p className="text-sm text-neutral-700 leading-relaxed">{detailMeasure.description}</p>
                </div>
              )}
              {detailMeasure.goal && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Doel</p>
                  <p className="text-sm text-neutral-700 leading-relaxed">{detailMeasure.goal}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {detailMeasure.cost_indication && (
                  <div className="p-3 bg-neutral-50 rounded-lg">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Kosten</p>
                    <p className="text-lg font-bold text-black">{'\u20AC'}{detailMeasure.cost_indication.toLocaleString()}</p>
                  </div>
                )}
                {detailMeasure.effectiveness != null && (
                  <div className="p-3 bg-neutral-50 rounded-lg">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Effectiviteit</p>
                    <p className="text-lg font-bold text-black">{detailMeasure.effectiveness}%</p>
                  </div>
                )}
              </div>

              {/* Cost of inaction */}
              {(detailMeasure.cost_of_inaction || detailMeasure.risk_id) && (
                <div className="p-3 border border-red-200 rounded-lg">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-red-500 mb-1">Financiele impact bij inactie</p>
                  {detailMeasure.cost_of_inaction ? (
                    <p className="text-lg font-bold text-red-700">{'\u20AC'}{detailMeasure.cost_of_inaction.toLocaleString()}</p>
                  ) : <p className="text-xs text-red-400">Nog niet berekend</p>}
                  {detailMeasure.is_preferred && (
                    <Badge className="bg-green-100 text-green-700 text-[10px] border-transparent mt-2">Voorkeursmaatregel</Badge>
                  )}
                </div>
              )}

              {/* Guided Autonomy: Impact + Status aanbeveling */}
              {detailMeasure.status === 'planned' && (
                <DecisionRow
                  label="Status"
                  value="Gepland, nog niet gestart"
                  type="recommended"
                  rationale={`Deze maatregel beschermt ${(detailMeasure.linked_risk_ids || []).length || 'onbekend aantal'} risico${(detailMeasure.linked_risk_ids || []).length !== 1 ? "'s" : ''}. Start zo snel mogelijk met de uitvoering.`}
                  onConfirm={async () => {
                    try {
                      await handleStatusChange(detailMeasure.id, 'in_progress');
                      setDetailMeasure(prev => prev ? { ...prev, status: 'in_progress' } : null);
                    } catch {}
                  }}
                  confirmLabel="Start uitvoering"
                  onEdit={() => { openEditModal(detailMeasure); setDetailMeasure(null); }}
                />
              )}

              {/* DecisionRow: Eigenaar */}
              <DecisionRow
                label="Eigenaar"
                value={detailMeasure.owner || detailMeasure.responsible || 'Geen eigenaar toegewezen'}
                type={(detailMeasure.owner || detailMeasure.responsible) ? 'auto' : 'required'}
                rationale={(detailMeasure.owner || detailMeasure.responsible) ? 'Toegewezen op basis van rol of team' : 'Zonder eigenaar wordt deze maatregel niet opgepakt'}
                confirmed={!!(detailMeasure.owner || detailMeasure.responsible)}
                onConfirm={(detailMeasure.owner || detailMeasure.responsible) ? undefined : () => { openEditModal(detailMeasure); setDetailMeasure(null); }}
                onEdit={() => { openEditModal(detailMeasure); setDetailMeasure(null); }}
                confirmLabel="Eigenaar toewijzen"
              />

              {/* DecisionRow: Deadline */}
              {detailMeasure.status !== 'verified' && detailMeasure.status !== 'implemented' && (
                <DecisionRow
                  label="Deadline"
                  value={detailMeasure.deadline || (() => {
                    const linkedCount = (detailMeasure.linked_risk_ids || []).length;
                    return linkedCount >= 3 ? 'Binnen 14 dagen' : linkedCount >= 1 ? 'Binnen 30 dagen' : 'Binnen 90 dagen';
                  })()}
                  type={detailMeasure.deadline ? 'auto' : 'recommended'}
                  rationale={detailMeasure.deadline ? 'Handmatig ingesteld' : `Aanbevolen op basis van ${(detailMeasure.linked_risk_ids || []).length} gekoppelde risico's`}
                  confirmed={!!detailMeasure.deadline}
                  onConfirm={detailMeasure.deadline ? undefined : () => { openEditModal(detailMeasure); setDetailMeasure(null); }}
                  onEdit={() => { openEditModal(detailMeasure); setDetailMeasure(null); }}
                  confirmLabel="Bevestigen"
                />
              )}

              {/* Execution method */}
              <div className="space-y-3 pt-4 border-t border-neutral-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400">Uitvoerwijze</p>
                {detailMeasure.execution_method ? (
                  <div className={`p-3 rounded-lg border ${detailMeasure.execution_method === 'self' ? 'border-neutral-200' : 'border-neutral-200'}`}>
                    <p className="text-xs font-semibold">
                      {detailMeasure.execution_method === 'self' ? 'Zelf uitvoeren' : 'Uitvoeren door leverancier'}
                    </p>
                    <p className="text-[10px] text-neutral-500 mt-0.5">
                      {detailMeasure.execution_method === 'self'
                        ? 'Er is een actiepunt aangemaakt in Strategie'
                        : detailMeasure.supplier_id ? 'Leverancier gekoppeld' : 'Leverancier nog niet gekozen'}
                    </p>
                  </div>
                ) : (
                  <>
                    <DecisionRow
                      label="Uitvoerwijze"
                      value="Nog niet gekozen"
                      type="required"
                      rationale="Kies of je de maatregel zelf uitvoert of een leverancier inschakelt"
                    />
                    <div className="grid grid-cols-2 gap-3" data-testid="execution-method-choice">
                      <button
                        onClick={async () => {
                          try {
                            const res = await api.post(`/ggrc/measures/${detailMeasure.id}/execute`, { execution_method: 'self' });
                            toast.success(res.data.message || 'Actiepunt aangemaakt in Strategie');
                            setDetailMeasure(prev => ({ ...prev, execution_method: 'self' }));
                            fetchData();
                          } catch { toast.error('Fout bij opslaan'); }
                        }}
                        className="p-3 rounded-lg border-2 border-neutral-200 hover:border-black hover:bg-neutral-50 transition-all text-left"
                        data-testid="execute-self-btn"
                      >
                        <CheckCircle className="h-5 w-5 text-neutral-600 mb-2" strokeWidth={1.5} />
                        <p className="text-sm font-semibold text-black">Zelf uitvoeren</p>
                        <p className="text-[10px] text-neutral-500 mt-0.5">Maakt automatisch een actiepunt aan in Strategie</p>
                      </button>
                      <button
                        onClick={async () => {
                          if (marketplaceSuppliers.length > 0) {
                            openHelpWizard(detailMeasure);
                            setDetailMeasure(null);
                          } else {
                            try {
                              await api.post(`/ggrc/measures/${detailMeasure.id}/execute`, { execution_method: 'supplier' });
                              toast.success('Leverancier-uitvoering gekozen');
                              setDetailMeasure(prev => ({ ...prev, execution_method: 'supplier' }));
                              fetchData();
                            } catch { toast.error('Fout bij opslaan'); }
                          }
                        }}
                        className="p-3 rounded-lg border-2 border-neutral-200 hover:border-black hover:bg-neutral-50 transition-all text-left"
                        data-testid="execute-supplier-btn"
                      >
                        <HandHelping className="h-5 w-5 text-neutral-600 mb-2" strokeWidth={1.5} />
                        <p className="text-sm font-semibold text-black">Leverancier inschakelen</p>
                        <p className="text-[10px] text-neutral-500 mt-0.5">Kies een leverancier met passende expertise</p>
                      </button>
                    </div>
                  </>
                )}

                {/* Contextuele leverancier-aanbeveling */}
                {marketplaceSuppliers.length > 0 && !detailMeasure.supplier_id && (
                  <div className="mt-2" data-testid="supplier-recommendation">
                    <DecisionRow
                      label="Aanbevolen leverancier"
                      value={(() => {
                        const domain = (detailMeasure.domain || '').toLowerCase();
                        const matched = marketplaceSuppliers.find(s =>
                          (s.specializations || []).some(sp => sp.toLowerCase().includes(domain)) ||
                          (s.name || '').toLowerCase().includes(domain)
                        ) || marketplaceSuppliers[0];
                        return matched ? `${matched.name || matched.company_name || 'Beschikbaar'}` : 'Leveranciers beschikbaar';
                      })()}
                      type="recommended"
                      rationale={`${marketplaceSuppliers.length} leverancier${marketplaceSuppliers.length !== 1 ? 's' : ''} beschikbaar met relevante expertise`}
                      onConfirm={() => { openHelpWizard(detailMeasure); setDetailMeasure(null); }}
                      confirmLabel="Bekijk leveranciers"
                    />
                  </div>
                )}
              </div>

              <Collapsible title="Relaties" testId="measure-relations">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-neutral-50 rounded-lg">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Assets</p>
                    <div className="space-y-1">
                      {detailMeasure.linked_asset_ids?.length > 0 ? detailMeasure.linked_asset_ids.map(id => (
                        <button key={id} onClick={() => { setDetailMeasure(null); navigate('/assets'); }} className="text-xs font-mono text-blue-600 hover:underline block">{id}</button>
                      )) : <span className="text-xs text-neutral-400">-</span>}
                    </div>
                  </div>
                  <div className="p-3 bg-neutral-50 rounded-lg">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Zwakke plekken</p>
                    <div className="space-y-1">
                      {detailMeasure.linked_vuln_ids?.length > 0 ? detailMeasure.linked_vuln_ids.map(id => (
                        <button key={id} onClick={() => { setDetailMeasure(null); navigate('/vulnerabilities'); }} className="text-xs font-mono text-blue-600 hover:underline block">{id}</button>
                      )) : <span className="text-xs text-neutral-400">-</span>}
                    </div>
                  </div>
                  <div className="p-3 bg-neutral-50 rounded-lg">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Aanvallen</p>
                    <div className="space-y-1">
                      {detailMeasure.linked_threat_ids?.length > 0 ? detailMeasure.linked_threat_ids.map(id => (
                        <button key={id} onClick={() => { setDetailMeasure(null); navigate('/threats'); }} className="text-xs font-mono text-blue-600 hover:underline block">{id}</button>
                      )) : <span className="text-xs text-neutral-400">-</span>}
                    </div>
                  </div>
                  <div className="p-3 bg-neutral-50 rounded-lg">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Risico's</p>
                    <div className="space-y-1">
                      {detailMeasure.linked_risk_ids?.length > 0 ? detailMeasure.linked_risk_ids.map(id => (
                        <button key={id} onClick={() => { setDetailMeasure(null); navigate('/risks'); }} className="text-xs font-mono text-blue-600 hover:underline block">R-{id}</button>
                      )) : <span className="text-xs text-neutral-400">-</span>}
                    </div>
                  </div>
                </div>
                {/* Framework bron */}
                {(detailMeasure.framework || detailMeasure.framework_id || detailMeasure.control_ref) && (
                  <div className="p-3 border border-neutral-200 rounded-lg mt-2">
                    <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1">Bron / Framework</p>
                    <p className="text-xs text-neutral-700">{detailMeasure.framework || getFrameworkLabel(detailMeasure.framework_id)} {detailMeasure.control_ref && `· ${detailMeasure.control_ref}`}</p>
                  </div>
                )}
              </Collapsible>
            </div>
          )}
      </RightPanel>

      <MeasureFormDialog
        open={modalOpen} onOpenChange={setModalOpen}
        editingMeasure={editingMeasure} wizardStep={wizardStep} setWizardStep={setWizardStep}
        formData={formData} setFormData={setFormData}
        actionLoading={actionLoading} handleSave={handleSave} suppliers={suppliers}
      />

      <HelpWizardDialog
        open={helpWizardOpen} onOpenChange={setHelpWizardOpen}
        helpWizardStep={helpWizardStep} helpRequestData={helpRequestData} setHelpRequestData={setHelpRequestData}
        measures={measures} matchedSuppliers={matchedSuppliers}
        selectedSupplierForHelp={selectedSupplierForHelp} setSelectedSupplierForHelp={setSelectedSupplierForHelp}
        actionLoading={actionLoading} getStatusBadge={getStatusBadge} toggleMeasureSelection={toggleMeasureSelection}
        handleHelpWizardNext={handleHelpWizardNext} handleHelpWizardBack={handleHelpWizardBack}
      />

      <FrameworkWizardDialog
        open={fwWizardOpen} onOpenChange={setFwWizardOpen}
        fwStep={fwStep} setFwStep={setFwStep}
        selectedFramework={selectedFramework} frameworkControls={frameworkControls}
        selectedControls={selectedControls} setSelectedControls={setSelectedControls}
        controlSearch={controlSearch} setControlSearch={setControlSearch} fwCreating={fwCreating}
        getAvailableFrameworks={getAvailableFrameworks} getAllFrameworks={getAllFrameworks}
        handleSelectFramework={handleSelectFramework} toggleControl={toggleControl}
        handleCreateFromControls={handleCreateFromControls} measures={measures}
      />
    </PageShell>
  );
};

export default MeasuresPage;
